// ContactsFileUploadSection.tsx
import {
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";

import { useEffect, useRef, useState } from "react";

import PropTypes from "prop-types";
import { tokens } from "../../../../../../theme";
import {
  autoMapHeadings,
  ErrorContact,
  FileContact,
  processContactsCSVFile,
  processHeadings,
} from "../../../../../stream-management/components/memory-table/utils/processContacts";
import { showErrorToast } from "../../../../../../components/common/ShowToast";
import { getFileExtension } from "../../../../../stream-management/components/memory-table/utils/getFileExtension";
import CloudUpload from "@mui/icons-material/CloudUpload";
import { ContactsMappingKeys } from "../../../../../stream-management/components/memory-table/components/ContactMappikngKeys";
import { VerifyContactsData } from "../../../../../stream-management/components/memory-table/components/ConfirmContactData";

interface Props {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fetchContacts: () => void; // was: () => Promise<void>
  changeModalProps: (toDefault: boolean) => void;
  streamId: number | null;
}

type displayModeType = "file-upload" | "confirm-data" | "mapping";

const STEPS = ["Upload", "Map Columns", "Verify"] as const;
const STEP_INDEX: Record<displayModeType, number> = {
  "file-upload": 0,
  mapping: 1,
  "confirm-data": 2,
};

export const ContactsFileUploadSection: React.FC<Props> = ({
  setOpen,
  fetchContacts,
  changeModalProps,
  streamId,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const contactsFileRef = useRef<HTMLInputElement | null>(null);
  const currentFile = useRef<File | null>(null);
  const [parsedContacts, setParsedContacts] = useState<FileContact[]>([]);
  const [displayMode, setDisplayMode] =
    useState<displayModeType>("file-upload");
  const [errorContacts, setErrorContacts] = useState<ErrorContact[]>([]);
  const [headingsFromFile, setHeadingFromFile] = useState<string[]>([]);
  const [fieldsMapped, setFieldsMapped] = useState({
    last_name: "",
    first_name: "",
    email: "",
    phone_number: "",
  });

  useEffect(() => {
    if (displayMode === "file-upload" || displayMode === "mapping") {
      changeModalProps(true);
    }
  }, [changeModalProps, displayMode]);

  const updateFieldsMapped = (
    name: keyof typeof fieldsMapped,
    value: string,
  ) => {
    setFieldsMapped((prev) => ({ ...prev, [name]: value }));
  };

  const openFileBrowseWindow = () => {
    contactsFileRef.current?.click();
  };

  const changed = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      showErrorToast("No file uploaded");
      return;
    }
    const file = e.target.files[0];
    const fileType = getFileExtension(file);

    if (fileType === null || file === undefined) {
      showErrorToast("Failed to get file extension. Try uploading file again");
      return;
    }

    if (fileType !== "csv") {
      showErrorToast("Only CSV files are supported for contact import");
      return;
    }

    currentFile.current = file;
    const headings = await processHeadings(file);
    setHeadingFromFile(headings);
    // Pre-fill the mapping so the Proceed guard passes without forcing the user to
    // re-pick columns that already match by name.
    setFieldsMapped(autoMapHeadings(headings));

    setDisplayMode("mapping");
  };

  const updateContactsDataFromFile = (
    contacts: FileContact[],
    errContacts: ErrorContact[],
  ) => {
    setParsedContacts(contacts);
    setErrorContacts(errContacts);
    changeModalProps(false);
    setDisplayMode("confirm-data");
  };

  const onBack = () => {
    setDisplayMode("file-upload");
  };

  const readContactsFile = async () => {
    // check if required fields have been mapped
    if (!fieldsMapped.first_name || !fieldsMapped.last_name) {
      showErrorToast("First name and last name are required");
      return;
    }

    if (!fieldsMapped.email && !fieldsMapped.phone_number) {
      showErrorToast("You must map at least Email or Phone Number");
      return;
    }

    if (currentFile.current === null) {
      showErrorToast("No file selected");
      return;
    }

    const fileType = getFileExtension(currentFile.current);

    if (fileType === "csv") {
      const fileData = await processContactsCSVFile(
        currentFile.current,
        fieldsMapped,
      );
      if (fileData.errorOccured) {
        showErrorToast(fileData.message);
      } else {
        updateContactsDataFromFile(fileData.contacts, fileData.errorContacts);
      }
    }
  };

  const removeErrorRecord = (id: number) => {
    setErrorContacts((prev) => prev.filter((contact) => contact.id !== id));
  };

  return (
    <Box sx={{ width: "100%", minHeight: 300, position: "relative" }}>
      <Stepper
        activeStep={STEP_INDEX[displayMode]}
        alternativeLabel
        sx={{ pt: 2, pb: 1 }}
      >
        {STEPS.map((step) => (
          <Step key={step}>
            <StepLabel>{step}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {displayMode === "file-upload" && (
        <>
          <input
            ref={contactsFileRef}
            style={{ width: "100%", height: "100%", display: "none" }}
            type="file"
            accept=".csv"
            onChange={(e) => changed(e)}
          />

          <Stack
            onClick={openFileBrowseWindow}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: -1,
              width: "100%",
              height: 300,
              "&:hover": {
                cursor: "pointer",
                backgroundColor: colors.primary[800],
                borderRadius: "0 0 1.1rem 1.1rem",
              },
            }}
          >
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <CloudUpload
                style={{
                  height: 60,
                  width: 60,
                }}
              />
            </Box>
            <Typography variant="body1" textAlign={"center"} mb={1}>
              Drag and drop your CSV file <br /> or click to upload
            </Typography>
          </Stack>
        </>
      )}

      {displayMode === "mapping" && (
        <ContactsMappingKeys
          headingsFromFile={headingsFromFile}
          fieldsMapped={fieldsMapped}
          updateFieldsMapped={updateFieldsMapped}
          onBack={onBack}
          readContactsFile={readContactsFile}
        />
      )}

      {displayMode === "confirm-data" && (
        <VerifyContactsData
          parsedContacts={parsedContacts}
          setParsedContacts={setParsedContacts}
          fetchContacts={fetchContacts}
          contactsFileRef={contactsFileRef}
          setOpen={setOpen}
          errorContacts={errorContacts}
          removeErrorRecord={removeErrorRecord}
          changed={changed}
          streamId={streamId}
        />
      )}
    </Box>
  );
};

ContactsFileUploadSection.propTypes = {
  setOpen: PropTypes.func.isRequired,
  fetchContacts: PropTypes.func.isRequired,
  changeModalProps: PropTypes.func.isRequired,
};